const BUILD_ID = typeof __APP_BUILD_ID__ !== "undefined" ? __APP_BUILD_ID__ : "dev";
const BUILD_STORAGE_KEY = "ch:build-id";
const RETRY_STORAGE_KEY = "ch:chunk-retry";
const MAX_RETRIES_PER_BUILD = 1;

declare const __APP_BUILD_ID__: string;

type ChunkErrorLike = {
  message?: string;
  reason?: { message?: string; name?: string };
};

function isChunkLoadFailure(err: unknown): boolean {
  const candidate = err as ChunkErrorLike;
  const message = candidate?.message || candidate?.reason?.message || "";
  return /Importing a module script failed|Failed to fetch dynamically imported module|ChunkLoadError|SCRIPT_LOAD_FAILED|Route chunk render error|dynamically imported module|Loading chunk/i.test(message);
}

function getRetryCount(): number {
  const raw = sessionStorage.getItem(RETRY_STORAGE_KEY);
  const parsed = raw ? Number.parseInt(raw, 10) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

function setRetryCount(next: number): void {
  sessionStorage.setItem(RETRY_STORAGE_KEY, String(next));
}

async function clearRuntimeCachesAndPwaSW(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const reg of registrations) {
        const scriptUrl = reg.active?.scriptURL || reg.installing?.scriptURL || reg.waiting?.scriptURL || "";
        if (scriptUrl && !scriptUrl.includes("firebase-messaging-sw.js")) {
          await reg.unregister();
        }
      }
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    // no-op: cache clear is best-effort only
  }
}

async function tryRecoverFromChunkFailure(): Promise<void> {
  const retries = getRetryCount();
  if (retries >= MAX_RETRIES_PER_BUILD) {
    return;
  }

  setRetryCount(retries + 1);
  await clearRuntimeCachesAndPwaSW();

  const current = new URL(window.location.href);
  current.searchParams.set("_v", BUILD_ID);
  window.location.replace(current.toString());
}

function refreshRetryStateForBuild(): void {
  const lastBuild = localStorage.getItem(BUILD_STORAGE_KEY);
  if (lastBuild !== BUILD_ID) {
    localStorage.setItem(BUILD_STORAGE_KEY, BUILD_ID);
    sessionStorage.removeItem(RETRY_STORAGE_KEY);
  }
}

export function installChunkLoadRecovery(): void {
  refreshRetryStateForBuild();

  window.addEventListener("vite:preloadError", (event: Event) => {
    event.preventDefault();
    void tryRecoverFromChunkFailure();
  });

  window.addEventListener("error", (event) => {
    if (isChunkLoadFailure((event as ErrorEvent).error ?? event)) {
      void tryRecoverFromChunkFailure();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadFailure(event.reason)) {
      event.preventDefault();
      void tryRecoverFromChunkFailure();
    }
  });
}
